import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { Fragment, type ReactElement, useId, useMemo, useState } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePermissoes } from '@/lib/permissoes'
import { cn } from '@/lib/utils'

import { BrandLogo, BrandMark } from './brand-logo'
import {
  AVISO_MODULO_FUTURO,
  type ItemNavegacao,
  rotaPertenceAoItem,
  SECOES_NAVEGACAO,
} from './nav-items'

/**
 * Sidebar do painel.
 *
 * O menu é FILTRADO POR PERMISSÃO: item que a pessoa não pode abrir não é
 * renderizado (ver `nav-items.ts`). Isso não é autorização — quem autoriza é a
 * API —, é não oferecer caminho que termina em 403.
 *
 * Superfície `--bg-surface`. O ÚNICO amarelo daqui é o item ativo: barra à
 * esquerda em `--primary` e texto em `--accent-foreground` (amarelo no tema
 * escuro, `brand-700` no claro, onde amarelo em texto reprovaria contraste).
 * Todo o resto é cinza — amarelo é acento, não preenchimento.
 *
 * Os itens vêm em GRUPOS que abrem e fecham (Reservas, Frota, Financeiro…). O
 * grupo da tela aberta abre sozinho; os que a pessoa abriu ficam abertos na
 * próxima visita (preferência deste navegador, nada mais).
 *
 * Três larguras, as três validadas pela DoD:
 *   - 375px  → não renderiza; a navegação vira gaveta (ver `AppShell`).
 *   - 768px  → trilho de ícones (`compacta`), rótulo no tooltip. Sem grupos
 *              que fecham — só um traço entre eles: ícone escondido atrás de
 *              um clique a mais não se acha.
 *   - 1440px → sidebar inteira com rótulos.
 */

const itemBase = cn(
  'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
  'outline-none focus-visible:ring-2 focus-visible:ring-ring',
)

const itemInativo = 'text-fg-secondary hover:bg-elevated hover:text-foreground'

const itemAtivo = cn(
  'bg-accent font-medium text-accent-foreground',
  'after:absolute after:left-0 after:top-1/2 after:h-5 after:w-0.5 after:-translate-y-1/2',
  'after:rounded-full after:bg-primary',
)

function ItemLink({
  item,
  compacta,
  aoNavegar,
}: {
  readonly item: ItemNavegacao
  readonly compacta: boolean
  readonly aoNavegar?: (() => void) | undefined
}): ReactElement {
  const Icone = item.icone

  if (item.para === null) {
    // `aria-disabled` em vez de `disabled`: botão desabilitado não emite evento
    // de ponteiro, e sem evento o tooltip que EXPLICA o porquê nunca apareceria.
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-disabled="true"
            className={cn(itemBase, 'cursor-not-allowed text-fg-muted opacity-70')}
          >
            <Icone className="size-4 shrink-0" aria-hidden="true" />
            <span className={cn('truncate', compacta && 'sr-only')}>{item.rotulo}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {item.rotulo} — {AVISO_MODULO_FUTURO}
        </TooltipContent>
      </Tooltip>
    )
  }

  const link = (
    <Link
      to={item.para}
      activeOptions={{ exact: true }}
      className={cn(itemBase, itemInativo)}
      activeProps={{ className: cn(itemBase, itemAtivo) }}
      onClick={aoNavegar}
    >
      <Icone className="size-4 shrink-0" aria-hidden="true" />
      <span className={cn('truncate', compacta && 'sr-only')}>{item.rotulo}</span>
    </Link>
  )

  if (!compacta) {
    return link
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.rotulo}</TooltipContent>
    </Tooltip>
  )
}

export interface SidebarNavProps {
  /** Trilho de ícones: esconde os rótulos e mostra tooltip. */
  readonly compacta?: boolean
  /** Chamado ao clicar em um link — a gaveta usa isso para se fechar. */
  readonly aoNavegar?: () => void
}

/** Os grupos que a pessoa deixou abertos — por navegador, como o tema. */
const GRUPOS_ABERTOS_STORAGE_KEY = 'locaveri:admin:menu-grupos-abertos'

function lerGruposAbertos(): ReadonlySet<string> {
  try {
    const salvo: unknown = JSON.parse(
      window.localStorage.getItem(GRUPOS_ABERTOS_STORAGE_KEY) ?? '[]',
    )
    return new Set(
      Array.isArray(salvo)
        ? salvo.filter((chave): chave is string => typeof chave === 'string')
        : [],
    )
  } catch {
    // Armazenamento bloqueado ou valor corrompido: o menu só não lembra.
    return new Set()
  }
}

function salvarGruposAbertos(grupos: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(GRUPOS_ABERTOS_STORAGE_KEY, JSON.stringify([...grupos]))
  } catch {
    // Idem: sem armazenamento, a escolha vale até recarregar a página.
  }
}

export function SidebarNav({ compacta = false, aoNavegar }: SidebarNavProps): ReactElement {
  const permissoes = usePermissoes()
  const caminho = useRouterState({ select: (estado) => estado.location.pathname })
  const prefixoDosIds = useId()

  const secoes = useMemo(
    () =>
      SECOES_NAVEGACAO.map((secao) => ({
        ...secao,
        itens: secao.itens.filter(
          (item) => item.permissao === null || permissoes.has(item.permissao),
        ),
      })).filter((secao) => secao.itens.length > 0),
    [permissoes],
  )

  const secaoAtual = secoes.find((secao) =>
    secao.itens.some((item) => rotaPertenceAoItem(caminho, item)),
  )?.chave

  // Dois conjuntos, de propósito. `abertas` é o que a pessoa ABRIU com um
  // clique — só isso vai para o armazenamento. `fechadas` é o que ela fechou
  // nesta visita, inclusive o grupo da tela aberta. Se o grupo que abriu
  // SOZINHO (por ser o da tela) fosse salvo como escolha, o menu iria abrindo
  // tudo com o tempo.
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(lerGruposAbertos)
  const [fechadas, setFechadas] = useState<ReadonlySet<string>>(() => new Set())

  // Trocou de tela para outro grupo: esse grupo abre, mesmo que tenha sido
  // fechado antes. Ajuste DURANTE a renderização, e não num efeito, para o
  // item ativo não piscar escondido por um quadro.
  const [secaoAnterior, setSecaoAnterior] = useState(secaoAtual)
  if (secaoAtual !== secaoAnterior) {
    setSecaoAnterior(secaoAtual)
    if (secaoAtual !== undefined && fechadas.has(secaoAtual)) {
      const novas = new Set(fechadas)
      novas.delete(secaoAtual)
      setFechadas(novas)
    }
  }

  function estaAberta(chave: string): boolean {
    return !fechadas.has(chave) && (abertas.has(chave) || chave === secaoAtual)
  }

  function alternar(chave: string): void {
    const abrir = !estaAberta(chave)
    const novasAbertas = new Set(abertas)
    const novasFechadas = new Set(fechadas)

    if (abrir) {
      novasAbertas.add(chave)
      novasFechadas.delete(chave)
    } else {
      novasAbertas.delete(chave)
      novasFechadas.add(chave)
    }

    setAbertas(novasAbertas)
    setFechadas(novasFechadas)
    salvarGruposAbertos(novasAbertas)
  }

  if (compacta) {
    return (
      <nav aria-label="Navegação principal" className="flex flex-col gap-1 px-2 py-3">
        {secoes.map((secao, indice) => (
          <Fragment key={secao.chave}>
            {indice > 0 ? <hr className="mx-2 my-1 border-border" /> : null}
            {secao.itens.map((item) => (
              <ItemLink key={item.chave} item={item} compacta aoNavegar={aoNavegar} />
            ))}
          </Fragment>
        ))}
      </nav>
    )
  }

  return (
    <nav aria-label="Navegação principal" className="flex flex-col gap-1 px-2 py-3">
      {secoes.map((secao) => {
        const itens = secao.itens.map((item) => (
          <ItemLink key={item.chave} item={item} compacta={false} aoNavegar={aoNavegar} />
        ))

        if (secao.rotulo === null) {
          return <Fragment key={secao.chave}>{itens}</Fragment>
        }

        const aberta = estaAberta(secao.chave)
        const idDoGrupo = `${prefixoDosIds}-${secao.chave}`

        return (
          <div key={secao.chave} className="flex flex-col gap-1">
            <button
              type="button"
              aria-expanded={aberta}
              aria-controls={idDoGrupo}
              onClick={() => {
                alternar(secao.chave)
              }}
              className={cn(
                'mt-2 flex items-center justify-between rounded-lg px-3 py-1.5',
                'text-xs font-medium uppercase tracking-wide text-fg-muted transition-colors',
                'outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              {secao.rotulo}
              <ChevronRight
                className={cn('size-3.5 transition-transform', aberta && 'rotate-90')}
                aria-hidden="true"
              />
            </button>
            {/* Sempre no DOM, só escondido: o `aria-controls` acima não pode
                apontar para um id que some quando o grupo fecha. */}
            <div id={idDoGrupo} hidden={!aberta} className="flex flex-col gap-1">
              {itens}
            </div>
          </div>
        )
      })}
    </nav>
  )
}

export interface SidebarProps {
  readonly className?: string
}

/**
 * Sidebar fixa do desktop. Em `md` é um trilho de 4rem; a partir de `lg`
 * abre para 16rem com os rótulos.
 */
export function Sidebar({ className }: SidebarProps): ReactElement {
  return (
    <aside
      className={cn(
        'hidden shrink-0 border-r border-border bg-sidebar md:flex md:w-16 md:flex-col lg:w-64',
        className,
      )}
    >
      <div className="flex h-14 items-center justify-center border-b border-border px-3 lg:justify-start">
        <BrandMark className="lg:hidden" />
        <BrandLogo className="hidden lg:block" />
      </div>

      {/* Abaixo de `lg` o trilho mostra só ícones. */}
      <div className="hidden flex-1 overflow-y-auto md:block lg:hidden">
        <SidebarNav compacta />
      </div>
      <div className="hidden flex-1 overflow-y-auto lg:block">
        <SidebarNav />
      </div>

      <p className="hidden px-4 py-3 text-xs text-fg-muted lg:block">Veículos blindados</p>
    </aside>
  )
}
